import React, { useState, useEffect, useMemo } from 'react';
import { flushSync } from 'react-dom';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { expensesAPI, lotesAPI, proveedoresAPI, loteGeneralAPI } from '../lib/api';
import { CATEGORIAS, SUBCATEGORIAS_ALIMENTO, FASES, UNIDADES, getCategoriasByFase } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { toast } from 'sonner';
import { CurrencyDollar, ArrowLeft, Bird, Plus, Building, UserPlus, Trash, PlusCircle, Warehouse } from '@phosphor-icons/react';

// Categorías para Lote General (sin alimento ni pollitos)
const CATEGORIAS_LOTE_GENERAL = CATEGORIAS.filter(c => !['alimento', 'pollitos'].includes(c.value));

export default function ExpenseNew() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preselectedLoteId = searchParams.get('lote_id');
  const isLoteGeneral = searchParams.get('lote_general') === 'true';
  
  const [lotes, setLotes] = useState([]);
  const [loteGeneral, setLoteGeneral] = useState(null);
  const [proveedores, setProveedores] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingLotes, setLoadingLotes] = useState(true);
  
  // Datos generales del gasto
  const [formData, setFormData] = useState({
    lote_id: preselectedLoteId || '',
    fase: '',
    categoria: '',
    subcategoria: '',
    factura: '',
    descripcion: '',
    proveedor_id: '',
    fecha: '',
    moneda: '',
    estado_pago: '',
  });
  
  // Items múltiples de la factura
  const [items, setItems] = useState([
    { id: 1, descripcion: '', cantidad: '', unidad: 'QQS', precio_unitario: '', tipo_alimento: '' }
  ]);
  
  // Modo: 'simple' o 'multiple'
  const [modoFactura, setModoFactura] = useState('simple');

  // Modal para crear nuevo proveedor
  const [showProveedorModal, setShowProveedorModal] = useState(false);
  const [creatingProveedor, setCreatingProveedor] = useState(false);
  const [nuevoProveedor, setNuevoProveedor] = useState({
    nombre: '',
    rnc: '',
    moneda_habitual: 'DOP',
    telefono: '',
    email: '',
  });

  useEffect(() => {
    loadLotes();
    loadProveedores();
  }, []);

  const loadProveedores = async () => {
    try {
      const response = await proveedoresAPI.getAll();
      setProveedores(response.data);
    } catch (error) {
      console.error('Error al cargar proveedores');
    }
  };

  const handleCrearProveedor = async () => {
    if (!nuevoProveedor.nombre.trim()) {
      toast.error('El nombre del proveedor es requerido');
      return;
    }
    
    setCreatingProveedor(true);
    try {
      const response = await proveedoresAPI.create(nuevoProveedor);
      const newProveedor = response.data;
      const newProveedorId = newProveedor.id;
      
      // Recargar lista de proveedores
      const proveedoresResponse = await proveedoresAPI.getAll();
      
      // Use flushSync to force synchronous state updates
      // This ensures the proveedores list is updated before we set the selection
      flushSync(() => {
        setProveedores(proveedoresResponse.data);
      });
      
      // Now set the proveedor_id after proveedores are updated
      flushSync(() => {
        setFormData((prev) => ({ ...prev, proveedor_id: newProveedorId }));
      });
      
      // Cerrar modal y limpiar formulario
      setShowProveedorModal(false);
      setNuevoProveedor({
        nombre: '',
        rnc: '',
        moneda_habitual: 'DOP',
        telefono: '',
        email: '',
      });
      
      toast.success('Proveedor creado y seleccionado');
    } catch (error) {
      toast.error('Error al crear proveedor');
    } finally {
      setCreatingProveedor(false);
    }
  };

  const loadLotes = async () => {
    try {
      const response = await lotesAPI.getAll('activo');
      // Filtrar el lote general de la lista normal
      const lotesNormales = response.data.filter(l => !l.es_general);
      setLotes(lotesNormales);
      
      // Si es para lote general, cargar el lote general
      if (isLoteGeneral) {
        const lgRes = await loteGeneralAPI.get();
        setLoteGeneral(lgRes.data);
        setFormData((prev) => ({ ...prev, lote_id: lgRes.data.id, fase: '5' }));
      } else if (lotesNormales.length === 1 && !preselectedLoteId) {
        setFormData((prev) => ({ ...prev, lote_id: lotesNormales[0].id }));
      }
    } catch (error) {
      toast.error('Error al cargar los lotes');
    } finally {
      setLoadingLotes(false);
    }
  };

  const handleChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
    
    // Reset categoria and subcategoria if fase changes
    if (name === 'fase') {
      setFormData((prev) => ({ ...prev, categoria: '', subcategoria: '', descripcion: '', factura: '' }));
    }
    
    // Reset subcategoria if categoria changes
    if (name === 'categoria' && value !== 'alimento') {
      setFormData((prev) => ({ ...prev, subcategoria: '' }));
    }
    
    // Set default unidad based on categoria
    if (name === 'categoria') {
      let defaultUnidad = 'QQS';
      
      if (value === 'alimento') {
        defaultUnidad = 'QQS';
      } else if (value === 'medicamentos') {
        defaultUnidad = 'ml';
      } else if (['mano_obra_vacuna', 'mano_obra_amarradores', 'nomina', 'gasto_amarradores', 'incentivo', 'alquiler', 'gastos_financieros', 'cena_venta'].includes(value)) {
        defaultUnidad = 'servicio';
      } else if (value === 'vacunas') {
        defaultUnidad = 'dosis';
      } else if (value === 'gasolina') {
        defaultUnidad = 'galon';
      }
      
      setFormData((prev) => ({ ...prev, descripcion: '' }));
      setItems([{ id: 1, descripcion: '', cantidad: '', unidad: defaultUnidad, precio_unitario: '', tipo_alimento: '' }]);
    }
  };

  // Funciones para manejar items múltiples
  const agregarItem = () => {
    const newId = Math.max(...items.map(i => i.id)) + 1;
    setItems([...items, { id: newId, descripcion: '', cantidad: '', unidad: items[0]?.unidad || 'QQS', precio_unitario: '', tipo_alimento: '' }]);
  };

  const eliminarItem = (id) => {
    if (items.length > 1) {
      setItems(items.filter(item => item.id !== id));
    }
  };

  const actualizarItem = (id, field, value) => {
    setItems(prevItems => prevItems.map(item => 
      item.id === id ? { ...item, [field]: value } : item
    ));
  };

  // Calcular total usando useMemo para que se actualice reactivamente
  const totalFactura = useMemo(() => {
    return items.reduce((total, item) => {
      const cantidad = parseFloat(item.cantidad) || 0;
      const precio = parseFloat(item.precio_unitario) || 0;
      return total + (cantidad * precio);
    }, 0);
  }, [items]);

  // Calcular subtotal de un item
  const calcularSubtotal = (item) => {
    const cantidad = parseFloat(item.cantidad) || 0;
    const precio = parseFloat(item.precio_unitario) || 0;
    return cantidad * precio;
  };
  
  // Get categorias filtered by selected fase
  // Para Lote General, usar categorías sin alimento ni pollitos
  const categoriasDisponibles = isLoteGeneral 
    ? CATEGORIAS_LOTE_GENERAL 
    : (formData.fase ? getCategoriasByFase(formData.fase) : []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validación según categoría
    const needsFactura = ['alimento', 'medicamentos'].includes(formData.categoria);
    
    if (!formData.lote_id || !formData.fase || !formData.categoria) {
      toast.error('Por favor complete todos los campos obligatorios');
      return;
    }
    
    if (!formData.moneda) {
      toast.error('Por favor seleccione la moneda');
      return;
    }
    
    if (!formData.fecha) {
      toast.error('Por favor seleccione la fecha');
      return;
    }
    
    // Validar items
    const itemsValidos = items.filter(item => item.cantidad && item.precio_unitario);
    if (itemsValidos.length === 0) {
      toast.error('Agregue al menos un artículo con cantidad y precio');
      return;
    }
    
    if (needsFactura && !formData.factura) {
      toast.error('Por favor ingrese el número de factura');
      return;
    }
    
    // Validar tipo de alimento para cada item cuando la categoría es alimento
    if (formData.categoria === 'alimento') {
      const itemsSinTipo = itemsValidos.filter(item => !item.tipo_alimento);
      if (itemsSinTipo.length > 0) {
        toast.error('Por favor seleccione el tipo de alimento para cada artículo');
        return;
      }
    }

    setLoading(true);
    try {
      // Si hay múltiples items, crear un gasto por cada item
      for (const item of itemsValidos) {
        // Construir descripción según categoría
        let descripcionFinal = '';
        let subcategoriaFinal = formData.subcategoria || null;
        
        if (formData.categoria === 'alimento') {
          // Para alimento, usar tipo_alimento del item
          const tipoAlimentoLabel = SUBCATEGORIAS_ALIMENTO.find(t => t.value === item.tipo_alimento)?.label || item.tipo_alimento;
          descripcionFinal = `Fact #${formData.factura}${tipoAlimentoLabel ? ' - ' + tipoAlimentoLabel : ''}${item.descripcion ? ' - ' + item.descripcion : ''}`;
          subcategoriaFinal = item.tipo_alimento || null;
        } else if (formData.categoria === 'medicamentos') {
          descripcionFinal = `Fact #${formData.factura} - ${item.descripcion || formData.descripcion}`;
        } else {
          descripcionFinal = item.descripcion || formData.descripcion || formData.categoria;
        }
        
        const payload = {
          lote_id: formData.lote_id,
          fase: formData.fase,
          categoria: formData.categoria,
          subcategoria: subcategoriaFinal,
          descripcion: descripcionFinal,
          cantidad: parseFloat(item.cantidad),
          unidad: item.unidad,
          precio_unitario: parseFloat(item.precio_unitario),
          proveedor: null,  // Legacy field
          proveedor_id: formData.proveedor_id || null,
          fecha: formData.fecha || null,  // Enviar como YYYY-MM-DD sin conversión a UTC
          moneda: formData.moneda,
          factura_numero: formData.factura || null,
          estado_pago: formData.estado_pago,
        };
        
        await expensesAPI.create(payload);
      }
      
      toast.success(`${itemsValidos.length} gasto(s) registrado(s) exitosamente`);
      
      if (isLoteGeneral) {
        navigate('/lote-general');
      } else if (preselectedLoteId) {
        navigate(`/lotes/${preselectedLoteId}`);
      } else {
        navigate('/gastos');
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Error al registrar el gasto';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const total = formData.cantidad && formData.precio_unitario
    ? (parseFloat(formData.cantidad) * parseFloat(formData.precio_unitario)).toFixed(2)
    : '0.00';

  const selectedLote = lotes.find((l) => l.id === formData.lote_id);
  const tasaCambio = selectedLote?.tasa_cambio || 61.5;
  const totalNum = parseFloat(total) || 0;
  const totalDOP = formData.moneda === 'USD' ? totalNum * tasaCambio : totalNum;
  const totalUSD = formData.moneda === 'USD' ? totalNum : totalNum / tasaCambio;

  return (
    <Layout>
      <div className="max-w-2xl mx-auto animate-fade-in" data-testid="new-expense-form">
        <div className="mb-6">
          <Link
            to={preselectedLoteId ? `/lotes/${preselectedLoteId}` : '/gastos'}
            className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1 mb-2"
          >
            <ArrowLeft size={14} />
            {preselectedLoteId ? 'Volver al Lote' : 'Volver a Gastos'}
          </Link>
          <h1 className="text-3xl font-bold tracking-tight uppercase" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            Nuevo Gasto
          </h1>
          <p className="text-muted-foreground">
            Registre un gasto para el lote seleccionado
          </p>
        </div>

        {!isLoteGeneral && lotes.length === 0 && !loadingLotes ? (
          <Card className="border-border/60">
            <CardContent className="py-12">
              <div className="empty-state">
                <Bird size={64} className="text-muted-foreground/50 mb-4" />
                <h3 className="text-xl font-semibold mb-2">No hay lotes activos</h3>
                <p className="text-muted-foreground mb-6">
                  Cree un lote primero para poder registrar gastos
                </p>
                <Link to="/lotes/nuevo">
                  <Button className="bg-[#E73425] hover:bg-[#D81C23]">
                    <Plus size={18} className="mr-2" />
                    Crear Lote
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        ) : (
          <Card className="border-border/60">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                {isLoteGeneral ? (
                  <>
                    <Warehouse size={24} className="text-[#E73425]" weight="fill" />
                    Gasto - Lote General
                  </>
                ) : (
                  <>
                    <CurrencyDollar size={24} className="text-[#F69031]" weight="fill" />
                    Información del Gasto
                  </>
                )}
              </CardTitle>
              <CardDescription>
                {isLoteGeneral 
                  ? 'Registre un gasto general que será distribuido entre lotes'
                  : 'Complete los datos del gasto a registrar'
                }
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Lote Selection - Oculto si es Lote General */}
                {!isLoteGeneral && (
                <div className="space-y-2">
                  <Label htmlFor="lote_id">Lote *</Label>
                  <Select
                    value={formData.lote_id}
                    onValueChange={(value) => handleChange('lote_id', value)}
                  >
                    <SelectTrigger data-testid="select-lote">
                      <SelectValue placeholder="Seleccione un lote" />
                    </SelectTrigger>
                    <SelectContent>
                      {lotes.map((lote) => (
                        <SelectItem key={lote.id} value={lote.id}>
                          {lote.nombre} - Día {lote.dias_transcurridos}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                )}

                {/* Phase Selection - Oculto si es Lote General (auto-asignado a fase 5) */}
                {!isLoteGeneral && (
                <div className="space-y-2">
                  <Label htmlFor="fase">Fase *</Label>
                  <Select
                    value={formData.fase}
                    onValueChange={(value) => handleChange('fase', value)}
                  >
                    <SelectTrigger data-testid="select-fase">
                      <SelectValue placeholder="Seleccione la fase" />
                    </SelectTrigger>
                    <SelectContent>
                      {FASES.map((fase) => (
                        <SelectItem key={fase.value} value={fase.value}>
                          <div>
                            <span className="font-medium">{fase.label}</span>
                            <span className="text-xs text-muted-foreground ml-2">
                              ({fase.description})
                            </span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                )}

                {/* Category Selection */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="categoria">Categoría *</Label>
                    <Select
                      value={formData.categoria}
                      onValueChange={(value) => handleChange('categoria', value)}
                      disabled={!isLoteGeneral && !formData.fase}
                    >
                      <SelectTrigger data-testid="select-categoria">
                        <SelectValue placeholder={isLoteGeneral || formData.fase ? "Seleccione categoría" : "Seleccione fase primero"} />
                      </SelectTrigger>
                      <SelectContent>
                        {categoriasDisponibles.map((cat) => (
                          <SelectItem key={cat.value} value={cat.value}>
                            {cat.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Helper text for fase */}
                {formData.fase && (
                  <p className="text-xs text-muted-foreground bg-muted/50 p-2 rounded">
                    💡 {FASES.find(f => f.value === formData.fase)?.description}
                  </p>
                )}

                {/* Factura and Description - varies by category */}
                {formData.categoria === 'alimento' && (
                  <div className="space-y-2">
                    <Label htmlFor="factura">Fact # *</Label>
                    <Input
                      id="factura"
                      placeholder="Número de factura"
                      value={formData.factura}
                      onChange={(e) => handleChange('factura', e.target.value)}
                      className="input-focus"
                      data-testid="expense-factura"
                    />
                  </div>
                )}

                {formData.categoria === 'medicamentos' && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="factura">Fact # *</Label>
                      <Input
                        id="factura"
                        placeholder="Número de factura"
                        value={formData.factura}
                        onChange={(e) => handleChange('factura', e.target.value)}
                        className="input-focus"
                        data-testid="expense-factura"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="descripcion">Descripción *</Label>
                      <Input
                        id="descripcion"
                        placeholder="Ej: VITAMINA A"
                        value={formData.descripcion}
                        onChange={(e) => handleChange('descripcion', e.target.value)}
                        className="input-focus"
                        data-testid="expense-descripcion"
                      />
                    </div>
                  </div>
                )}

                {formData.categoria && !['alimento', 'medicamentos'].includes(formData.categoria) && (
                  <div className="space-y-2">
                    <Label htmlFor="descripcion">Descripción</Label>
                    <Textarea
                      id="descripcion"
                      placeholder="Descripción del gasto"
                      value={formData.descripcion}
                      onChange={(e) => handleChange('descripcion', e.target.value)}
                      className="input-focus resize-none"
                      rows={2}
                      data-testid="expense-descripcion"
                    />
                  </div>
                )}

                {/* Items de la factura */}
                <div className="space-y-4">
                  <Label className="text-base font-semibold">Artículos de la Factura</Label>
                  
                  {items.map((item, index) => (
                    <div key={item.id} className="border rounded-lg p-4 bg-muted/30 space-y-3">
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-medium text-muted-foreground">
                          Artículo {index + 1}
                        </span>
                        {items.length > 1 && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => eliminarItem(item.id)}
                            className="h-8 w-8 p-0 text-red-500 hover:text-red-700"
                          >
                            <Trash size={16} />
                          </Button>
                        )}
                      </div>
                      
                      {/* Descripción del artículo (solo para medicamentos u otros) */}
                      {formData.categoria && formData.categoria !== 'alimento' && (
                        <div className="space-y-2">
                          <Label>Descripción</Label>
                          <Input
                            placeholder="Descripción del artículo"
                            value={item.descripcion}
                            onChange={(e) => actualizarItem(item.id, 'descripcion', e.target.value)}
                            className="input-focus"
                          />
                        </div>
                      )}
                      
                      {/* Tipo de alimento (solo cuando categoría es alimento) */}
                      {formData.categoria === 'alimento' && (
                        <div className="space-y-2">
                          <Label>Tipo de Alimento *</Label>
                          <Select
                            value={item.tipo_alimento}
                            onValueChange={(value) => actualizarItem(item.id, 'tipo_alimento', value)}
                          >
                            <SelectTrigger className={!item.tipo_alimento ? 'border-amber-300' : ''}>
                              <SelectValue placeholder="Seleccione tipo de alimento" />
                            </SelectTrigger>
                            <SelectContent>
                              {SUBCATEGORIAS_ALIMENTO.map((tipo) => (
                                <SelectItem key={tipo.value} value={tipo.value}>
                                  {tipo.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                      
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <div className="space-y-2">
                          <Label>Cantidad *</Label>
                          <Input
                            type="number"
                            step="0.01"
                            min="0.01"
                            placeholder="Ej: 100"
                            value={item.cantidad}
                            onChange={(e) => actualizarItem(item.id, 'cantidad', e.target.value)}
                            className="input-focus"
                          />
                        </div>

                        <div className="space-y-2">
                          <Label>Unidad</Label>
                          <Select
                            value={item.unidad}
                            onValueChange={(value) => actualizarItem(item.id, 'unidad', value)}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {UNIDADES.map((u) => (
                                <SelectItem key={u.value} value={u.value}>
                                  {u.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>

                        <div className="space-y-2">
                          <Label>Precio Unitario *</Label>
                          <Input
                            type="number"
                            step="0.01"
                            min="0.01"
                            placeholder="Ej: 0.50"
                            value={item.precio_unitario}
                            onChange={(e) => actualizarItem(item.id, 'precio_unitario', e.target.value)}
                            className="input-focus"
                          />
                        </div>

                        <div className="space-y-2">
                          <Label>Subtotal</Label>
                          <div className="h-10 px-3 flex items-center bg-muted rounded-md text-sm font-medium">
                            {formData.moneda === 'USD' ? '$' : 'RD$'}{' '}
                            {calcularSubtotal(item).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                  
                  {/* Botón agregar artículo y Total */}
                  <div className="flex justify-between items-center p-4 bg-[#E73425]/10 rounded-lg">
                    <span className="font-semibold">Total de la Factura:</span>
                    <div className="flex items-center gap-4">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={agregarItem}
                        className="text-emerald-600 hover:text-emerald-700 border-emerald-300"
                      >
                        <PlusCircle size={16} className="mr-1" />
                        Agregar Artículo
                      </Button>
                      <span className="text-xl font-bold text-[#E73425]">
                        {formData.moneda === 'USD' ? '$' : formData.moneda === 'DOP' ? 'RD$' : ''}{' '}
                        {totalFactura.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Moneda */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="moneda">Moneda *</Label>
                    <Select
                      value={formData.moneda}
                      onValueChange={(value) => handleChange('moneda', value)}
                    >
                      <SelectTrigger data-testid="select-moneda" className={!formData.moneda ? 'border-amber-300' : ''}>
                        <SelectValue placeholder="Seleccione moneda" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="USD">USD ($)</SelectItem>
                        <SelectItem value="DOP">DOP (RD$)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Provider, Payment Status and Date */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="proveedor_id" className="flex items-center gap-2">
                      <Building size={14} />
                      Proveedor
                    </Label>
                    <Select
                      value={formData.proveedor_id}
                      onValueChange={(value) => handleChange('proveedor_id', value === "none" ? "" : value)}
                    >
                      <SelectTrigger data-testid="select-proveedor">
                        <SelectValue placeholder="Seleccionar proveedor..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Sin proveedor</SelectItem>
                        {proveedores.map((p) => (
                          <SelectItem key={p.id} value={p.id}>
                            {p.nombre} ({p.moneda_habitual})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <button
                      type="button"
                      onClick={() => setShowProveedorModal(true)}
                      className="text-xs text-[#F69031] hover:underline flex items-center gap-1"
                    >
                      <UserPlus size={14} />
                      Crear nuevo proveedor
                    </button>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="estado_pago">Estado de Pago</Label>
                    <Select
                      value={formData.estado_pago}
                      onValueChange={(value) => handleChange('estado_pago', value)}
                    >
                      <SelectTrigger data-testid="select-estado-pago">
                        <SelectValue placeholder="Seleccionar estado..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pagado">Pagado (Contado)</SelectItem>
                        <SelectItem value="credito">A Crédito</SelectItem>
                      </SelectContent>
                    </Select>
                    {formData.estado_pago === 'credito' && formData.proveedor_id && (
                      <p className="text-xs text-amber-600">
                        Se sumará al balance del proveedor
                      </p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="fecha">Fecha *</Label>
                    <Input
                      id="fecha"
                      type="date"
                      value={formData.fecha}
                      onChange={(e) => handleChange('fecha', e.target.value)}
                      className={`input-focus ${!formData.fecha ? 'border-amber-300' : ''}`}
                      data-testid="expense-fecha"
                    />
                  </div>
                </div>

                {/* Total Preview */}
                <div className="bg-muted/50 rounded-lg p-4 border border-border/60">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Total del Gasto</p>
                      <p className="text-xs text-muted-foreground">
                        {items.length} artículo(s) - {formData.moneda === 'USD' ? 'USD' : 'DOP'}
                      </p>
                      {selectedLote && (
                        <p className="text-xs text-muted-foreground mt-1">
                          Tasa: 1 USD = {tasaCambio} DOP
                        </p>
                      )}
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold text-[#F69031]" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                        {formData.moneda === 'USD' ? '$' : 'RD$'} {totalFactura.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        ≈ {formData.moneda === 'USD' 
                          ? `RD$ ${(totalFactura * tasaCambio).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                          : `US$ ${(totalFactura / tasaCambio).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                        }
                      </p>
                    </div>
                  </div>
                </div>

                {/* Buttons */}
                <div className="flex gap-3 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => navigate(isLoteGeneral ? '/lote-general' : (preselectedLoteId ? `/lotes/${preselectedLoteId}` : '/gastos'))}
                    className="flex-1"
                  >
                    Cancelar
                  </Button>
                  <Button
                    type="submit"
                    className="flex-1 bg-[#E73425] hover:bg-[#D81C23] btn-animate"
                    disabled={loading}
                    data-testid="submit-expense"
                  >
                    {loading ? (
                      <div className="spinner h-5 w-5" />
                    ) : (
                      <>
                        <CurrencyDollar size={18} className="mr-2" />
                        Registrar Gasto
                      </>
                    )}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Modal para crear nuevo proveedor */}
      <Dialog open={showProveedorModal} onOpenChange={setShowProveedorModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus size={24} className="text-[#F69031]" />
              Crear Nuevo Proveedor
            </DialogTitle>
            <DialogDescription>
              Complete los datos del proveedor. Los datos del gasto se mantendrán.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="prov-nombre">Nombre del Proveedor *</Label>
              <Input
                id="prov-nombre"
                placeholder="Ej: Alimentos Del Campo S.R.L."
                value={nuevoProveedor.nombre}
                onChange={(e) => setNuevoProveedor(prev => ({ ...prev, nombre: e.target.value }))}
                data-testid="modal-prov-nombre"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="prov-rnc">RNC</Label>
                <Input
                  id="prov-rnc"
                  placeholder="000-00000-0"
                  value={nuevoProveedor.rnc}
                  onChange={(e) => setNuevoProveedor(prev => ({ ...prev, rnc: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="prov-moneda">Moneda Habitual</Label>
                <Select
                  value={nuevoProveedor.moneda_habitual}
                  onValueChange={(value) => setNuevoProveedor(prev => ({ ...prev, moneda_habitual: value }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="DOP">DOP (Pesos)</SelectItem>
                    <SelectItem value="USD">USD (Dólares)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="prov-telefono">Teléfono</Label>
                <Input
                  id="prov-telefono"
                  placeholder="809-000-0000"
                  value={nuevoProveedor.telefono}
                  onChange={(e) => setNuevoProveedor(prev => ({ ...prev, telefono: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="prov-email">Email</Label>
                <Input
                  id="prov-email"
                  type="email"
                  placeholder="correo@empresa.com"
                  value={nuevoProveedor.email}
                  onChange={(e) => setNuevoProveedor(prev => ({ ...prev, email: e.target.value }))}
                />
              </div>
            </div>
          </div>
          
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowProveedorModal(false)}>
              Cancelar
            </Button>
            <Button
              className="bg-[#F69031] hover:bg-[#E5802A]"
              onClick={handleCrearProveedor}
              disabled={creatingProveedor || !nuevoProveedor.nombre.trim()}
            >
              {creatingProveedor ? (
                <div className="spinner h-4 w-4" />
              ) : (
                <>
                  <Plus size={16} className="mr-2" />
                  Crear Proveedor
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
